import React, { useEffect, useState, useRef } from "react";
import TomSelect from "tom-select";
import { del } from "@hotwired/stimulus";
import { CATEGORY_LIST_MAX_HEIGHT } from "../constants/stylingConstants.js";
import { apiClient } from "../api_client"


export default function PrizeGroups({ id, teamsPrizesVal, onSuccess }) {
  const [teamsPrizes, setTeamsPrizes] = useState(String(teamsPrizesVal));
  const [savedTP, setSavedTP] = useState(teamsPrizesVal);
  const [prizeGroups, setPrizeGroups] = useState([]);
  const [selectedPrizeGroup, setSelectedPrizeGroup] = useState(null);
  const [catList, setCatList] = useState([]);
  const [name, setName] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const DEFAULT_CATEGORIES = [
    "MU9", "MU11", "MU13", "MU15", "MU17", "MU19", "MU21", "MU23",
    "MSEN", "MV40", "MV45", "MV50", "MV55", "MV60", "MV65", "MV70", "MV75", "MV80",
    "WU9", "WU11", "WU13", "WU15", "WU17", "WU19", "WU21", "WU23",
    "WSEN", "WV40", "WV45", "WV50", "WV55", "WV60", "WV65", "WV70", "WV75", "WV80"
  ];


  useEffect(() => {
    refresh();
  }, [id]);

  // pre fill cat select
  useEffect(() => {
    if (selectedPrizeGroup) {
      setSelectedCategories(selectedPrizeGroup.categories.map(c => c.id));
    }
  }, [selectedPrizeGroup]);

  const refresh = async () => {
    try {
      const result = await apiClient.prizes.getGroups(id);
      setPrizeGroups(result.prize_groups || []);
      setCatList(result.cat_list || []);
    } catch (err) {
      console.error("Failed to fetch prize groups:", err);
    }
  };

  const loadGroups = async () => {
    try {
      const result = await apiClient.prizes.loadOld(id)
      setPrizeGroups(result.prize_groups);
      alert(`Loaded previous groups!`);
    } catch (err) {
      console.error("Failed to fetch prize groups:", err);
      alert(err.message || "Failed to get previous settings!");
    }
  };

  const saveTeams = async (e) => {
    e.preventDefault();
    const numTeams = Number(teamsPrizes);
    try {
      const updatedEvent = await apiClient.prizes.updateTeams(id, numTeams);

      setSavedTP(updatedEvent.teams_for_prizes);
      alert(`Entrants for teams updated to ${updatedEvent.teams_for_prizes}!`);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save entrants for team prizes");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    try {
      await apiClient.prizes.uploadGroup(id, name);

      refresh();
      setName("");
      alert("Prize group created successfully!");
    } catch (err) {
      alert(err.message || "Failed to create prize group");
    }
  };

  const handleDelete = async (pgId) => {
    try {
      await apiClient.prizes.deleteGroup(id, pgId);

      refresh();
      if (selectedPrizeGroup?.id === pgId) setSelectedPrizeGroup(null);
      alert("Prize group deleted successfully!");
    } catch (err) {
      alert(err.message || "Deletion failed");
    }
  };

  const handleSaveCategories = async () => {
    if (!selectedPrizeGroup) return alert("Select a prize group first!");

    try {
      await apiClient.prizes.uploadCategories(id, selectedPrizeGroup.id, selectedCategories);

      const updatedCats = catList.filter((c) =>
        selectedCategories.includes(c.id)
      );

      // Update front end
      const updatedPG = {
        ...selectedPrizeGroup,
        categories: updatedCats,
      };
      setSelectedPrizeGroup(updatedPG);

      // Update prize table
      setPrizeGroups((prev) =>
        prev.map((pg) =>
          pg.id === updatedPG.id ? updatedPG : pg
        )
      );

      alert("Categories updated successfully!");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to update categories");
    }
  };


  const handleCheckboxChange = (e, id) => {
    if (e.target.checked) {
      setSelectedCategories((prev) => [...prev, id]);
    } else {
      setSelectedCategories((prev) => prev.filter((catId) => catId !== id));
    }
  };

  const handleSaveName = async (pgId) => {
    try {
      await apiClient.prizes.updateGroupName(id, pgId, editingName);

      setPrizeGroups((prev) =>
        prev.map((pg) =>
          pg.id === pgId ? { ...pg, name: editingName } : pg
        )
      );

      if (selectedPrizeGroup?.id === pgId) {
        setSelectedPrizeGroup({
          ...selectedPrizeGroup,
          name: editingName,
        });
      }
      setOriginalName(editingName);
    } catch (err) {
      alert(err.message || "Failed to update prize group name");
    }
  };

  return (
    <div className="d-flex flex-wrap gap-4 p-3">
      <div className="card p-4 flex-fill">
        {/* Create Form */}
        <div className="form-con mb-2">
          <h4>Create Prize Group</h4>
          <button 
            onClick={loadGroups}
            className="mt-1 btn btn-primary">
              Load old prize groups
          </button>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Name:
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-control"
                required
              />
              </label>
            </div>
            <button className="btn btn-success" type="submit">
              <i className="fa-solid fa-plus"></i>
              <span className="visually-hidden">Add Prize Group</span>
            </button>
          </form>
        </div>

        {/* Prize Groups Table */}
        <div className="table-con mt-3">
          <h4>Prize Groups</h4>
          <table className="table table-striped table-bordered table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Categories</th>
                <th>Select</th>
                <th style={{ minWidth: "40px", textAlign: "center" }}>
                  <i className="fa-regular fa-trash-can"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              {prizeGroups.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    No prize groups yet
                  </td>
                </tr>
              )}
              {prizeGroups.map((pg) => (
                <tr key={pg.id}>
                  <td>
                    {editingGroupId === pg.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                      />
                    ) : (
                      pg.name
                    )}
                  </td>
                  <td>{pg.categories.length}</td>
                  <td style={{ minWidth: "40px", textAlign: "center" }}>
                    {editingGroupId === pg.id ? (
                      <button
                        aria-label="Save Prize Group"
                        className="btn btn-sm btn-success"
                        disabled={editingName.trim() === originalName.trim()}
                        onClick={() => handleSaveName(pg.id)}
                      >
                        <i className="fa-solid fa-floppy-disk"></i>
                      </button>
                    ) : (
                      <button
                        aria-label="Edit Prize Group"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setSelectedPrizeGroup(pg);
                          setEditingGroupId(pg.id);
                          setEditingName(pg.name);
                          setOriginalName(pg.name);
                        }}
                      >
                        <i className="fa-solid fa-pen-to-square"></i>
                      </button>
                    )}
                  </td>
                  <td style={{ minWidth: "40px", textAlign: "center" }}>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(pg.id)}
                    >
                      <i className="fa-solid fa-trash-can"></i>
                      <span className="visually-hidden">Delete Prize Group</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
         <div className="table-con mb-4">
          {/* Categories Table */}
          <h4>Categories</h4>
          {selectedPrizeGroup ? (
            <table className="table cat-table table-striped table-bordered table-sm">
              <thead>
                <tr>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {selectedPrizeGroup.categories.length === 0 && (
                  <tr>
                    <td colSpan={2} className="text-center">No categories yet</td>
                  </tr>
                )}
                {selectedPrizeGroup.categories.map((cat, i) => (
                  <tr key={i}>
                    <td>{cat.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>Select a prize group to see its categories</p>
          )}
        </div>
      </div>

      <div className="card p-4 flex-fill">
        {/* Add Categories Table */}
        <div className="table-con mb-4">
          {selectedPrizeGroup ? (
            <div className="form-con">
            <h4>Add Categories to {selectedPrizeGroup.name}</h4>
            <div className="overflow-auto" style={{height: CATEGORY_LIST_MAX_HEIGHT}}>
              <table className="table edit-table table-striped">
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                      Category Name
                    </th>
                    <th style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                      Select
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {catList.map((cat) => (
                    <tr key={cat.id}>
                      <td>{cat.name}</td>
                      <td>
                        <input
                          type="checkbox"
                          value={cat.id}
                          checked={selectedCategories.includes(cat.id)}
                          onChange={(e) => handleCheckboxChange(e, cat.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2">
              <button className="btn btn-success" onClick={handleSaveCategories}
                data-testid="save-categories-btn">
                <i className="fa-solid fa-floppy-disk"></i>
                <span className="ms-2">Save</span>
              </button>
            </div>
          </div>
          ) : (
            <div className="overflow-auto" style={{height: CATEGORY_LIST_MAX_HEIGHT}}>
              <table className="table cat-table table-striped table-bordered table-sm">
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, background: "white", zIndex: 1 }}>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {DEFAULT_CATEGORIES.map((cat) => (
                    <tr key={cat} className="text-muted">
                      <td>{cat}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Team Prizes Card */}
      <div className="card p-4 w-100 mt-4">
        <h4>Team Prizes</h4>

        <form onSubmit={saveTeams}>
          <div className="mb-3">
            <label className="form-label">
              Entrants per team to include in prize calculations
            </label>
            <input
              type="text"
              id="team-entrants"
              value={teamsPrizes}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  setTeamsPrizes(val);
                }
              }}
              className="form-control"
              required
            />
            {teamsPrizes === "" && (
              <div className="text-danger">Please enter a number.</div>
            )}
          </div>

          <button
            className="btn btn-primary"
            type="submit"
            disabled={teamsPrizes === String(savedTP)}
          >
            {teamsPrizes === String(savedTP) ? "Saved" : "Save"}
            <i className="fa-solid fa-floppy-disk ms-2"></i>
          </button>
        </form>
      </div>
      {onSuccess && (
        <div className="mt-4 text-end">
          <button
            className="btn btn-success"
            onClick={onSuccess}
          >
            Finish Event Setup
          </button>
        </div>
      )}
    </div>
  );
}